import React, { useState, useEffect } from 'react';
import { kbApi, formatApiError } from '../../services/api';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Plus,
  Search,
  Filter,
  BookOpen,
  Eye,
  ArrowLeft,
  Pencil,
  Trash2,
  Loader2,
  Tag,
  Clock
} from 'lucide-react';

export default function KBPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: [],
    is_public: true
  });
  const [tagInput, setTagInput] = useState('');

  const canManage = user?.role && ['super_admin', 'admin', 'technician'].includes(user.role);
  const canDelete = user?.role && ['super_admin', 'admin'].includes(user.role);

  useEffect(() => {
    fetchArticles();
  }, [filterCategory]);

  const fetchArticles = async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (searchQuery) params.search = searchQuery;
      const res = await kbApi.getAll(params);
      setArticles(res.data);
    } catch (error) {
      console.error('Fetch KB error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchArticles();
  };

  const openArticle = async (article) => {
    try {
      const res = await kbApi.getById(article.id);
      setSelectedArticle(res.data);
    } catch (error) {
      console.error('Fetch article error:', error);
    }
  };

  const openCreateModal = () => {
    setEditingArticle(null);
    setForm({ title: '', content: '', category: 'general', tags: [], is_public: true });
    setTagInput('');
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (article) => {
    setEditingArticle(article);
    setForm({
      title: article.title || '',
      content: article.content || '',
      category: article.category || 'general',
      tags: article.tags || [],
      is_public: article.is_public !== false
    });
    setTagInput('');
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      if (editingArticle) {
        await kbApi.update(editingArticle.id, form);
      } else {
        await kbApi.create(form);
      }
      setShowModal(false);
      setSelectedArticle(null);
      setLoading(true);
      fetchArticles();
    } catch (error) {
      setFormError(formatApiError(error));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm(t('delete') + '?')) return;
    try {
      await kbApi.delete(articleId);
      setSelectedArticle(null);
      setLoading(true);
      fetchArticles();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const getCategoryBadge = (category) => {
    const styles = {
      hardware: 'bg-blue-100 text-[#2A52C4] border-[#2A52C4]',
      software: 'bg-purple-100 text-purple-700 border-purple-700',
      network: 'bg-cyan-100 text-cyan-700 border-cyan-700',
      general: 'bg-gray-100 text-[#71717A] border-[#71717A]'
    };
    return styles[category] || styles.general;
  };

  const filteredArticles = articles.filter(article => {
    if (searchQuery) {
      const s = searchQuery.toLowerCase();
      return (
        article.title?.toLowerCase().includes(s) ||
        article.content?.toLowerCase().includes(s) ||
        article.tags?.some(tag => tag.toLowerCase().includes(s))
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A52C4]"></div>
      </div>
    );
  }

  // Article detail view
  if (selectedArticle) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedArticle(null)} className="text-[#71717A]">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back_to_kb')}
          </Button>
        </div>
        <Card className="border border-[#E4E4E7] shadow-none">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {selectedArticle.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-[#71717A]">
                  <Badge className={`${getCategoryBadge(selectedArticle.category)} border text-xs`}>
                    {selectedArticle.category}
                  </Badge>
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {selectedArticle.views} {t('views')}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(selectedArticle.created_at).toLocaleDateString('it-IT')}
                  </span>
                  {selectedArticle.created_by_name && (
                    <span>di {selectedArticle.created_by_name}</span>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => openEditModal(selectedArticle)} className="border-[#E4E4E7]">
                    <Pencil className="w-4 h-4 mr-1" /> {t('edit')}
                  </Button>
                  {canDelete && (
                    <Button variant="outline" size="sm" onClick={() => handleDelete(selectedArticle.id)} className="border-red-200 text-[#DC2626]">
                      <Trash2 className="w-4 h-4 mr-1" /> {t('delete')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {selectedArticle.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedArticle.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs border-[#E4E4E7]">
                    <Tag className="w-3 h-3 mr-1" />{tag}
                  </Badge>
                ))}
              </div>
            )}
            <div className="prose max-w-none text-[#0F172A] whitespace-pre-wrap">
              {selectedArticle.content}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Articles list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('kb_articles')}
          </h1>
          <p className="text-[#71717A] mt-1">{filteredArticles.length} articoli</p>
        </div>
        {canManage && (
          <Button onClick={openCreateModal} className="bg-[#2A52C4] hover:bg-[#2A52C4]/90 text-white">
            <Plus className="w-4 h-4 mr-2" />
            {t('create_article')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border border-[#E4E4E7] shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder={t('search_kb')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 border-[#E4E4E7]"
              />
            </div>
            <Select value={filterCategory || 'all'} onValueChange={(v) => { setFilterCategory(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-full sm:w-[180px] border-[#E4E4E7]">
                <Filter className="w-4 h-4 mr-2 text-[#71717A]" />
                <SelectValue placeholder={t('category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('category')}</SelectItem>
                <SelectItem value="general">Generale</SelectItem>
                <SelectItem value="hardware">Hardware</SelectItem>
                <SelectItem value="software">Software</SelectItem>
                <SelectItem value="network">Network</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Articles Grid */}
      {filteredArticles.length === 0 ? (
        <div className="text-center py-12 text-[#71717A]">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('no_articles')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredArticles.map((article) => (
            <Card
              key={article.id}
              className="border border-[#E4E4E7] shadow-none hover:border-[#2A52C4] cursor-pointer transition-colors"
              onClick={() => openArticle(article)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <Badge className={`${getCategoryBadge(article.category)} border text-xs`}>
                    {article.category}
                  </Badge>
                  {article.is_public && (
                    <Badge variant="outline" className="text-xs border-[#E4E4E7] text-[#71717A]">
                      {t('public').split(' ')[0]}
                    </Badge>
                  )}
                </div>
                <h3 className="font-medium text-[#0F172A] mb-2 line-clamp-2">{article.title}</h3>
                <p className="text-sm text-[#71717A] line-clamp-3 mb-3">
                  {article.content}
                </p>
                {article.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {article.tags.slice(0, 3).map(tag => (
                      <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 border-[#E4E4E7]">
                        {tag}
                      </Badge>
                    ))}
                    {article.tags.length > 3 && (
                      <span className="text-[10px] text-[#71717A]">+{article.tags.length - 3}</span>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-[#71717A] pt-3 border-t border-[#F4F4F5]">
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" /> {article.views || 0}
                  </span>
                  <span>{new Date(article.created_at).toLocaleDateString('it-IT')}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px] bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingArticle ? t('edit_article') : t('create_article')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            {formError && (
              <div className="p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md">
                {formError}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">{t('title')} *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                placeholder="Come collegare la VPN..."
                className="h-10 border-[#E4E4E7]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-[#0F172A]">{t('category')}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-10 border-[#E4E4E7]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Generale</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="network">Network</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 flex items-end">
                <div className="flex items-center gap-2 h-10">
                  <Checkbox
                    id="is_public"
                    checked={form.is_public}
                    onCheckedChange={(checked) => setForm({ ...form, is_public: !!checked })}
                  />
                  <Label htmlFor="is_public" className="text-sm text-[#0F172A] cursor-pointer">{t('public')}</Label>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">Contenuto *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                required
                rows={8}
                placeholder="Scrivi il contenuto dell'articolo..."
                className="border-[#E4E4E7]"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-[#0F172A]">Tag</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  placeholder="Aggiungi tag e premi Invio"
                  className="h-10 border-[#E4E4E7]"
                />
                <Button type="button" variant="outline" onClick={addTag} className="border-[#E4E4E7] shrink-0">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs border-[#E4E4E7] cursor-pointer hover:border-red-300" onClick={() => removeTag(tag)}>
                      {tag} x
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter className="mt-6 pt-4 border-t border-[#E4E4E7]">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-[#E4E4E7]">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-[#2A52C4] hover:bg-[#2A52C4]/90 text-white" disabled={formLoading}>
                {formLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> ...</>
                ) : (
                  t('save')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
